import { jwtDecode } from 'jwt-decode';
import { sortBy } from 'lodash';
import { createContext, ReactNode, useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { AUTH_TOKEN } from 'src/config';
import { SUPER_ADMIN_COMPANY_NAME, SUPER_ADMIN_ID } from 'src/constants/admin';
import { IsSuperAdmin } from 'src/constants/permissionsList';
import { AuthContextValue, User } from 'src/types/auth';
import { Companies } from 'src/types/Companies';
import {
  getImpersonatedClientIdFromUser,
  getImpersonatedCompanyIdFromUser,
  getSessionClientId,
  isUserImpersonating,
} from '../utils/authImpersonation';
import axios from '../utils/axios';
import { getUserCompanyDetails } from '../utils/transformation';
export const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

type AuthProviderProps = {
  children: ReactNode;
};

interface CompanyFromMessage {
  companyId: number;
  companyName: string;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [tokenFromMessage, setTokenFromMessage] = useState('');
  const [companiesFromMessage, setCompaniesFromMessage] = useState<Companies>([]);
  const messageHandler = useCallback((event: MessageEvent<string>) => {
    try {
      const data: { jwt: string; companies?: CompanyFromMessage[] } = JSON.parse(event.data);
      if (data.jwt) {
        setTokenFromMessage(data.jwt);
        setCompaniesFromMessage(
          data.companies?.map((company) => ({
            companyId: company.companyId.toString(),
            companyName: company.companyName,
          })) || []
        );
      }
    } catch (error) {}
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, [messageHandler]);

  const [
    user,
    companies,
    token,
    loggedInCompanyId,
    companyId,
    clientId,
    loggedInUserId,
    isImpersonating,
    impersonatedCompanyId,
    impersonatedClientId,
    sessionClientId,
    isShuster,
    companyName,
    isSuperAdmin,
    isLoggedInAsSuperAdmin,
    siteName,
  ] = useMemo(() => {
    const token = tokenFromMessage || AUTH_TOKEN;
    const user: User = token ? jwtDecode(token) : ({ LocaleId: '1033' } as User);

    const companies: Companies = user?.Companies?.length
      ? sortBy(getUserCompanyDetails(user), ['companyName'])
      : companiesFromMessage;
    const loggedInCompanyId = user?.LoggedInCompanyId;
    const companyName = user?.CompanyName || '';
    const companyId = user?.CompanyId;
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    const isShuster = user?.SiteID === '126';
    const isSuperAdmin = !!user?.Role?.includes(IsSuperAdmin[0]);
    // True when the original (pre-impersonation) login was the Super Admin company.
    // `LoggedInCompanyId` / `LoggedInCompanyName` are preserved across impersonation,
    // so this stays true even after a Super Admin impersonates another user.
    const isLoggedInAsSuperAdmin =
      loggedInCompanyId === SUPER_ADMIN_ID ||
      user?.LoggedInCompanyName === SUPER_ADMIN_COMPANY_NAME;
    const siteName = user?.SiteName;
    const clientId = user?.ClientId;
    const loggedInUserId = user?.LoggedInUserId;
    const isImpersonating = isUserImpersonating(user);
    const impersonatedCompanyId = getImpersonatedCompanyIdFromUser(user);
    const impersonatedClientId = getImpersonatedClientIdFromUser(user);
    const sessionClientId = getSessionClientId(clientId);
    return [
      user,
      companies,
      token,
      loggedInCompanyId,
      companyId,
      clientId,
      loggedInUserId,
      isImpersonating,
      impersonatedCompanyId,
      impersonatedClientId,
      sessionClientId,
      isShuster,
      companyName,
      isSuperAdmin,
      isLoggedInAsSuperAdmin,
      siteName,
    ];
  }, [companiesFromMessage, tokenFromMessage]);

  return (
    <AuthContext.Provider
      value={{
        user,
        companies,
        token,
        loggedInCompanyId,
        companyId,
        clientId,
        loggedInUserId,
        isImpersonating,
        impersonatedCompanyId,
        impersonatedClientId,
        sessionClientId,
        isShuster,
        companyName,
        isSuperAdmin,
        isLoggedInAsSuperAdmin,
        siteName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
